import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDealDetails, useDealStats } from './useDealDetails';
import { Link, useParams } from 'react-router-dom';
import {
  formatIndianNumber,
  getRupee,
  getRupeeWithDecimal,
  numDifferentiation,
  twoDecimalNumber,
} from '../../utils/numbers';
import { formatDate } from '../../utils/date';
import Image from '../../assets/Image';
import {
  ArrowBack,
  CreditCardOffIcon,
  VerifiedIcon,
  InfoIcon,
  InfoOutlinedIcon,
  DownloadForOfflineOutlinedIcon,
  TimelineOutlinedIcon,
  ScreenRotationAltOutlinedIcon,
  ArticleOutlinedIcon,
  CloseOutlinedIcon,
} from '../../assets/mui';
import { DealDocumentType } from '../Deals/types';
import Input from '../../components/Fields/Input';
import KYCButton from '../Onboarding/KYCButton';
import ReinvestBanner from '../../assets/reinvest-banner.png';
import MultiCardAsset from '../../assets/multiCardAsset.webp';
import { NetworkManager } from '../../services';
import useGetPartnerProfile, {
  getCreateMIFPaymentLink,
  getCreateWalletPaymentLink,
  getPgVaWalletBalance,
} from '../../hooks/useGetPartnerProfile';
import { kycError } from '../../utils/toast';
import SelectClient from './SelectClient';
import { Dialog, DialogContent, DialogTitle, Skeleton } from '@mui/material';
import { useClientVirtualAccount } from '../Clients/useClient';
import MultiTag from './MultiTag';
import DealCompanies from './DealCompanies';
import MultiFaqs from './MultiFaqs';
import Checkbox from '../../components/Fields/Checkbox';
import { DEAL_DETAIL_EVENTS } from './constants';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import { useAnalytics } from '../../hooks/useAnalytics';
import ClientDrawer from '../Clients/ClientDrawer/ClientDrawer';
import toast from 'react-hot-toast';
import { SecurityMeasureCard } from '../../components/molecules/SecurityMeasureCard';
import IDFaqs from './IDFaqs';
import { useProfile } from '../../context/ProfileContext';
import { RemoteConfigContext } from '../../context/RemoteConfigContext';
import { CLIENT_DETAIL_EVENTS } from '../ClientDetail/constants';
import OfflineBoltRoundedIcon from '@mui/icons-material/OfflineBoltRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { CustomTooltip, YieldTooltipContent } from '../BondsDealDetail/constants';
import { downloadDealDetailPDF } from './CanvasToPdf';
import DownSellYieldScrubber, { getDownSellMinYield } from './DownSellYieldScrubber';

interface DealDetailProps {}

const DealDetail: React.FC<DealDetailProps> = () => {
  // Helper function to sanitize investment amount (removes commas and other non-numeric characters except decimal point)
  const sanitizeAmount = (amount: string | undefined): number => {
    if (!amount) return 0;
    const sanitized = amount.replace(/[^0-9.]/g, '');
    return parseFloat(sanitized) || 0;
  };

  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [agreement, setAgreement] = useState('');
  const [isInvestmentLoading, setInvestmentLoading] = useState(false);
  const [client, setClient] = useState<any>(null);
  const [amountError, setAmountError] = useState(null);
  const [maxAmount, setMaxAmount] = useState();
  const [investmentAmount, setInvestmentAmount] = useState<string>();
  const amountSectionRef = useRef<HTMLDivElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const [investmentId, setInvestmentId] = useState();
  const [totalReturns, setTotalReturns] = useState(null);
  const [commission, setCommission] = useState(null);
  const [commissionRate, setCommissionRate] = useState(null);
  const [isCommissionVisible, setIsCommissionVisible] = useState(true);
  const [orderType, setOrderType] = useState<'COMMISSION' | 'DOWN_SELL'>('COMMISSION');
  const [targetYield, setTargetYield] = useState('');
  const [savedDownSells, setSavedDownSells] = useState<any[]>([]);
  const [downSellProfit, setDownSellProfit] = useState<number | null>(null);
  const [grossDownSellProfit, setGrossDownSellProfit] = useState<number | null>(null);
  const [blendedDownSellSharePct, setBlendedDownSellSharePct] = useState<number | null>(null);
  const [platformRetainedDownSellProfit, setPlatformRetainedDownSellProfit] = useState<number | null>(
    null,
  );
  const [parentYield, setParentYield] = useState<number | null>(null);
  const [isDownSellDragging, setIsDownSellDragging] = useState(false);
  const [isDownSellTermsUpdating, setIsDownSellTermsUpdating] = useState(false);
  const [reinvest, setReinvest] = useState(false);
  const [reinvestModal, setReinvestModal] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const [userPaymentLink, setUserPaymentLink] = useState('');
  const [investmentStatus, setInvestmentStatus] = useState<'error' | 'success' | null>(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const clientId = client?.clientPublicId;
  const { dealId, financeType } = useParams();
  const { data, isLoading } = useDealDetails(dealId, financeType);
  const { data: dealDetails } = data || {};
  const { track } = useAnalytics();

  const isPooling = financeType === 'POOLING';
  const { remoteConfig } = useContext(RemoteConfigContext);
  const flatPercentageDealPartner = remoteConfig?.flatPercentageDealPartner;
  const showPartnerArrearsCard = remoteConfig?.showPartnerArrearsCard;

  const { data: statsData } = useDealStats(dealId, isPooling);
  const { data: stats } = statsData || {};

  const { data: partner } = useGetPartnerProfile();

  const { data: virtualAccountData, isLoading: virtualAccountLoading } =
    useClientVirtualAccount(clientId);

  const partnerId = partner?.data?.profile?.publicId;

  const { profileData } = useProfile();

  const {
    accountNumber: walletAccountNumber,
    bankName: walletBankName,
    branchDetails: walletBranchDetails,
    ifscCode: walletIfscCode,
  } = virtualAccountData?.data || {};

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      track({
        eventName: 'Deal_Detail_Page_View',
        eventProperties: { dealId: dealId, financeType: financeType },
      });
    }
  }, []);

  const handleWalletRecharge = async () => {
    try {
      setButtonLoading(true);
      track({ eventName: DEAL_DETAIL_EVENTS.Deal_Detail_Generate_Payment_Link });
      if (!clientId) {
        toast.error('Please select a client', {
          duration: 2500,
          position: 'top-center',
        });
        return;
      }
      if (!investmentAmount) {
        toast.error('Please enter an investment amount', {
          duration: 2500,
          position: 'top-center',
        });
        return;
      }
      const pgVaBalance = await getPgVaWalletBalance({ amount: sanitizeAmount(investmentAmount) });

      if (!pgVaBalance) {
        toast.error('Online payments down. Please use bank transfer', {
          duration: 3500,
          position: 'top-center',
        });

        return handleWalletOpen();
      }

      track({ eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Generate_Link });

      const response = await getCreateWalletPaymentLink({
        clientPublicId: clientId,
        amount: sanitizeAmount(investmentAmount),
      });

      setUserPaymentLink(response?.data);
      setIsRechargeModalOpen(true);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Something went wrong, please try again later';
      toast.error(errorMessage, {
        duration: 2500,
        position: 'top-center',
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const {
    name,
    maturityDate,
    minInvestment,
    commission: initialCommission,
    commissionPercentage,
    constituentDTOList,
    xirr,
    perAnnumReturns,
    roi,
    downSellEnabled,
  } = dealDetails || {};

  const showCommissionRow =
    !!profileData?.clientActions?.showEarnedIncome && orderType !== 'DOWN_SELL';

  const hasEarningsSection = showCommissionRow || !isPooling;

  const commissionToggle = hasEarningsSection && (
    <button
      type="button"
      onClick={() => setIsCommissionVisible((prev) => !prev)}
      className="-mr-1 p-1 rounded-full text-stone-400 hover:text-stone-600 hover:bg-black/5 transition-colors"
      aria-label={isCommissionVisible ? 'Hide earnings' : 'Show earnings'}
      aria-expanded={isCommissionVisible}
      data-html2canvas-ignore
    >
      {isCommissionVisible ? (
        <VisibilityOutlinedIcon sx={{ height: '18px', width: '18px' }} />
      ) : (
        <VisibilityOffOutlinedIcon sx={{ height: '18px', width: '18px' }} />
      )}
    </button>
  );

  const clearDownSellBreakdown = () => {
    setDownSellProfit(null);
    setGrossDownSellProfit(null);
    setBlendedDownSellSharePct(null);
    setPlatformRetainedDownSellProfit(null);
  };

  const setDownSellBreakdown = (result: any) => {
    if (result?.partnerDownSellProfit != null) setDownSellProfit(result.partnerDownSellProfit);
    setGrossDownSellProfit(result?.grossDownSellProfit ?? null);
    setBlendedDownSellSharePct(result?.blendedDownSellSharePct ?? null);
    setPlatformRetainedDownSellProfit(result?.platformRetainedDownSellProfit ?? null);
  };

  // Ops toggle XOR: down-sell deals have no commission option and vice versa.
  useEffect(() => {
    if (downSellEnabled) {
      setOrderType('DOWN_SELL');
      setCommission(null);
      setCommissionRate(null);
      setTargetYield((prev) => {
        if (prev || minInvestment?.netIRR == null) return prev;
        const dealYield = Number(minInvestment.netIRR);
        const minY = getDownSellMinYield(dealYield, savedDownSells);
        const starter = Math.max(minY, Math.round((dealYield - 0.01) * 100) / 100);
        return String(twoDecimalNumber(starter));
      });
    } else {
      setOrderType('COMMISSION');
      clearDownSellBreakdown();
    }
    // Intentionally omit targetYield — only seed once when down-sell mode enables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downSellEnabled, minInvestment?.netIRR, savedDownSells]);

  const {
    backedByImageUrl,
    clientImageUrl,
    vendorName,
    maturedDeals,
    onTimeRepaymentsPercentage,
    repaymentsDone,
    totalDeals,
    totalRaisedByCompany,
    totalRepayments,
    documents,
  } = stats || {};

  const hasDocuments = !!documents && documents.length > 0;

  const isClientWalletEnough = useMemo(() => {
    const sanitizedInvestmentAmount = sanitizeAmount(investmentAmount);
    return client?.balance >= minInvestment?.amount && client?.balance >= sanitizedInvestmentAmount;
  }, [minInvestment?.amount, client?.balance, investmentAmount]);

  const copyText = useMemo(() => {
    return `Please add money to the following beneficiary to make investments on Tap Invest. (Note that money should be deposited from a bank account matching the name below.):
    Name: ${client?.name}
    Bank Name: ${walletBankName}
    Branch Name: ${walletBranchDetails}
    Account Number: ${walletAccountNumber}
    IFSC Code: ${walletIfscCode}`;
  }, [client?.name, walletBankName, walletBranchDetails, walletAccountNumber, walletIfscCode]);

  const waText = useMemo(() => {
    return `Please add money to the following beneficiary to make investments on Tap Invest. (Note that money should be deposited from a bank account matching the name below.): %0a
    Name: ${client?.name} %0a
    Bank Name: ${walletBankName} %0a
    Branch Name: ${walletBranchDetails} %0a
    Account Number: ${walletAccountNumber} %0a
    IFSC Code: ${walletIfscCode}`;
  }, [client?.name, walletBankName, walletBranchDetails, walletAccountNumber, walletIfscCode]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(copyText);
    toast.success('Copied to clipboard');
  };

  const handleInvest = async () => {
    if (investmentAmount && clientId && partnerId) {
      setButtonLoading(true);
      try {
        const investmentResponse = await NetworkManager.Call({
          method: 'post',
          path: NetworkManager.PATH.createInvestment.replace('{{dealId}}', dealId),
          data: {
            amount: sanitizeAmount(investmentAmount),
            clientPublicId: clientId,
            partnerPublicId: partnerId,
            stayInvested: reinvest,
            paymentMode: 'WALLET',
            financeType,
            ...(orderType === 'DOWN_SELL'
              ? { orderType: 'DOWN_SELL', targetYieldPct: Number(targetYield) }
              : { orderType: 'COMMISSION' }),
          },
        });
        setInvestmentId(investmentResponse?.data.investmentId);
        setInvestmentStatus('success');
        track({ eventName: DEAL_DETAIL_EVENTS.Investment_Success_Screen });
      } catch (error) {
        setInvestmentStatus('error');
        const errorMessage = error?.response?.data?.message || 'Something went wrong';
        kycError(errorMessage);
      } finally {
        setButtonLoading(false);
      }
    }
  };

  const handleWalletRechargeWithInvestment = async () => {
    if (investmentAmount && clientId) {
      setButtonLoading(true);
      try {
        if (!investmentAmount) {
          toast.error('Please enter an investment amount', {
            duration: 2500,
            position: 'top-center',
          });
          return;
        }

        const pgVaBalance = await getPgVaWalletBalance({
          amount: sanitizeAmount(investmentAmount),
        });

        if (!pgVaBalance) {
          toast.error('Online payments down. Please use bank transfer', {
            duration: 3500,
            position: 'top-center',
          });

          return handleWalletOpen();
        }

        const response = await getCreateMIFPaymentLink({
          clientPublicId: clientId,
          amount: sanitizeAmount(investmentAmount),
          dealId,
          reinvestmentOn: reinvest,
          isMulti: isPooling,
        });

        setUserPaymentLink(response?.data + '&financeType=' + financeType);
        setIsPaymentOpen(true);
        track({ eventName: CLIENT_DETAIL_EVENTS.Client_Wallet_Generate_Link });
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || 'Something went wrong, please try again later';
        toast.error(errorMessage, {
          duration: 2500,
          position: 'top-center',
        });
      } finally {
        setButtonLoading(false);
      }
    }
  };

  const handleReinvestModal = (e) => {
    e.preventDefault();
    setReinvestModal(true);
  };

  const handleOptIn = async () => {
    setReinvest(true);
    setReinvestModal(false);
    (await isClientWalletEnough) ? handleInvest() : handleWalletRechargeWithInvestment();
  };

  const handleOptOut = () => {
    setReinvest(false);
    setReinvestModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInvestmentLoading(true);
    await handleWalletRechargeWithInvestment();
    setInvestmentLoading(false);
  };

  const handleInvestmentAmount = useCallback(
    async (e) => {
      const input = e.target.value;

      // Allow only numbers, a single decimal point and only two decimal places
      const sanitizedAmount = input
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*?)\..*/g, '$1')
        .replace(/(\.\d{2})\d+/g, '$1');

      setInvestmentAmount(sanitizedAmount);

      if (maxAmount && parseFloat(sanitizedAmount) > parseFloat(maxAmount)) {
        setAmountError(`Max investment amount is ₹${twoDecimalNumber(maxAmount)}`);
      } else if (maxAmount && parseFloat(sanitizedAmount) === parseFloat(maxAmount)) {
        setAmountError(null);
      } else {
        setAmountError(null);
        if (!sanitizedAmount || parseFloat(sanitizedAmount) === 0) {
          setAmountError(null);
          setTotalReturns(null);
          setCommission(null);
          setCommissionRate(null);
        } else if (parseFloat(sanitizedAmount) < minInvestment?.amount) {
          setAmountError('Less than minimum investment amount');
          setTotalReturns(null);
          setCommission(null);
          setCommissionRate(null);
        } else {
          const investmentResponse = await NetworkManager.Call({
            method: 'get',
            path: NetworkManager.PATH.investmentTerms.replace('{{dealId}}', dealId),
            params: {
              amount: sanitizedAmount,
              financeType,
              ...(orderType === 'DOWN_SELL' && targetYield
                ? { targetYieldPct: targetYield }
                : {}),
            },
          });

          const totalReturns = investmentResponse?.data?.result?.returnAmount;
          setAmountError(null);
          setMaxAmount(investmentResponse?.data?.result?.maxInvestment?.amount);
          if (totalReturns) {
            setTotalReturns(totalReturns);
          }
          const commission = investmentResponse?.data?.result?.commission;
          if (commission) {
            setCommission(commission);
          }
          const commissionRate = investmentResponse?.data?.result?.commissionPercentage;
          if (commissionRate) {
            setCommissionRate(commissionRate);
          }
          const result = investmentResponse?.data?.result;
          setDownSellBreakdown(result);
          if (result?.parentYieldPct != null) {
            setParentYield(result.parentYieldPct);
          }
        }
      }
    },
    [dealId, financeType, minInvestment?.amount, maxAmount, orderType, targetYield],
  );

  const handlePaymentClose = () => {
    setIsPaymentOpen(false);
    setUserPaymentLink('');
  };

  const handleWalletRechargeClose = () => {
    setIsRechargeModalOpen(false);
    setUserPaymentLink('');
  };

  const handleClose = () => {
    setInvestmentStatus(null);
    setClient(null);
    setInvestmentAmount(undefined);
    setInvestmentId(undefined);
    setOrderType('COMMISSION');
    setTargetYield('');
    clearDownSellBreakdown();
  };

  const handleWalletOpen = () => {
    track({ eventName: DEAL_DETAIL_EVENTS.Deal_Detail_Wallet_Details });
    setWalletOpen(true);
  };

  const handleWalletClose = () => setWalletOpen(false);

  const handleReinvestChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      track({ eventName: DEAL_DETAIL_EVENTS.Deal_Detail_Reinvest });
    }
    setReinvest(event.target.checked);
  };

  const openServiceAgreement = async () => {
    let url;
    if (agreement) {
      url = agreement;
    } else {
      const agreementResponse = await NetworkManager.Call({
        method: 'get',
        path: NetworkManager.PATH.getPartnerDealContract
          .replace('{{financeType}}', financeType)
          .replace('{{dealId}}', dealId),
      });

      setAgreement(agreementResponse?.data.link);

      url = agreementResponse?.data.link;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.click();
    URL.revokeObjectURL(url);
  };

  // reset investment amount and error under amount input field when client is changed
  const handleClientChange = () => {
    setInvestmentAmount(undefined);
    setAmountError(null);
  };

  const focusInvestmentAmount = useCallback((message = 'Please enter investment amount') => {
    setAmountError(message);
    amountSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => amountInputRef.current?.focus(), 280);
  }, []);

  useEffect(() => {
    if (!dealId || isPooling) return undefined;
    NetworkManager.Call({
      method: 'get',
      path: NetworkManager.PATH.downSellTerms.replace('{{dealId}}', dealId),
    })
      .then((res) => {
        const rows = res?.data?.result ?? res?.data ?? [];
        setSavedDownSells(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setSavedDownSells([]));
    return undefined;
  }, [dealId, isPooling]);

  useEffect(() => {
    if (orderType !== 'DOWN_SELL' || !investmentAmount || !targetYield || !dealId || isPooling) {
      return undefined;
    }
    const yieldNum = Number(targetYield);
    if (!Number.isFinite(yieldNum) || yieldNum <= 0) {
      return undefined;
    }
    if (minInvestment?.netIRR != null && yieldNum >= Number(minInvestment.netIRR)) {
      return undefined;
    }

    setIsDownSellTermsUpdating(true);
    const delay = isDownSellDragging ? 300 : 0;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      NetworkManager.Call({
        method: 'get',
        path: NetworkManager.PATH.investmentTerms.replace('{{dealId}}', dealId),
        params: {
          amount: sanitizeAmount(investmentAmount),
          financeType,
          targetYieldPct: targetYield,
        },
      })
        .then((investmentResponse) => {
          if (cancelled) return;
          const result = investmentResponse?.data?.result;
          if (result?.returnAmount) setTotalReturns(result.returnAmount);
          if (result?.clientReturnAmount) setTotalReturns(result.clientReturnAmount);
          setDownSellBreakdown(result);
          if (result?.parentYieldPct != null) setParentYield(result.parentYieldPct);
          setCommission(null);
          setCommissionRate(null);
        })
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setIsDownSellTermsUpdating(false);
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    orderType,
    targetYield,
    investmentAmount,
    dealId,
    financeType,
    isPooling,
    isDownSellDragging,
    minInvestment?.netIRR,
  ]);

  // Move useMemo before the conditional return
  const selectClientProps = useMemo(
    () => ({
      client,
      setClient,
      eventName: DEAL_DETAIL_EVENTS.Deal_Detail_Client_Select,
      onChange: handleClientChange,
    }),
    [client],
  );

  if (isLoading) return null;
  const handleUseWalletBalance = async () => {
    const formattedAmount = twoDecimalNumber(client?.balance || 0);
    setInvestmentAmount(formattedAmount);
    const sanitizedAmount = sanitizeAmount(formattedAmount);

    setAmountError(null);
    setTotalReturns(null);
    setCommission(null);
    setCommissionRate(null);

    if (sanitizedAmount < minInvestment?.amount) {
      setAmountError('Less than minimum investment amount');
    } else {
      try {
        const investmentResponse = await NetworkManager.Call({
          method: 'get',
          path: NetworkManager.PATH.investmentTerms.replace('{{dealId}}', dealId),
          params: {
            amount: sanitizedAmount,
            financeType,
            ...(orderType === 'DOWN_SELL' && targetYield
              ? { targetYieldPct: targetYield }
              : {}),
          },
        });

        const result = investmentResponse?.data?.result;
        const fetchedMaxAmount = result?.maxInvestment?.amount;

        setMaxAmount(fetchedMaxAmount);
        if (result?.returnAmount) {
          setTotalReturns(result.returnAmount);
        }
        if (result?.commission) {
          setCommission(result.commission);
        }
        if (result?.commissionPercentage) {
          setCommissionRate(result.commissionPercentage);
        }
        if (orderType === 'DOWN_SELL') {
          setDownSellBreakdown(result);
        }

        if (fetchedMaxAmount && sanitizedAmount > parseFloat(fetchedMaxAmount)) {
          setAmountError(`Max investment amount is ₹${twoDecimalNumber(fetchedMaxAmount)}`);
        }
      } catch (error) {
        console.error('Error fetching investment terms', error);
      }
    }

    track({
      eventName: DEAL_DETAIL_EVENTS.User_wallet_balance_selected,
      eventProperties: { clientId: client?.clientPublicId, investmentAmount: formattedAmount },
    });
  };

  const totalRaisedByCompanyFormatted = numDifferentiation(totalRaisedByCompany);

  return (
    <div className="my-6 max-w-7xl mx-auto">
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-8 xl:gap-12">
        <div className="col-span-2">
          <Link
            to="/deals"
            className="bg-green-700/10 flex items-center justify-center mb-6 p-1.5 h-9 w-9 rounded-full text-green-700"
          >
            <ArrowBack fontSize="small" />
          </Link>
          {/* Heading */}
          <div>
            <div className="w-20 h-20 overflow-hidden rounded  mb-6" data-html2canvas-ignore>
              {dealDetails.company?.logoUrl && (
                <Image
                  // @ts-ignore
                  src={dealDetails.company.logoUrl as any}
                  alt={dealDetails.name}
                  // className="w-full h-full object-contain"
                />
              )}
            </div>
            <div className="flex gap-4 items-center mb-4">
              <h1 className="text-xl font-semibold text-slate-800">
                {isPooling ? name : vendorName}
                {/* TODO: Uncomment after prod data is fixed */}
                {/* <span className="text-slate-600"><ArrowBack /></span> {customerName} */}
              </h1>
              {isPooling && <MultiTag />}
            </div>
            <div className="flex justify-between gap-4 items-center">
              <p className=" text-stone-500 max-w-2xl">{dealDetails.miniSummary}</p>
              <button
                className="bg-green-700 text-white text-sm px-4 py-2 rounded-md whitespace-nowrap shrink-0"
                data-html2canvas-ignore
                onClick={downloadDealDetailPDF}
              >
                Download PDF
              </button>
            </div>
          </div>
          {/* Company Details */}
          {!isPooling && (
            <div className="flex lg:mt-9">
              <div className="flex mt-auto w-full min-h-0 flex-col bg-black/[.02] rounded-lg border border-stone-300">
                <div className="grid grid-cols-2 xl:grid-cols-4 border-b border-b-stone-300">
                  <div className="p-4 md:p-6 border-r border-r-stone-300 max-xl:border-b max-xl:border-b-stone-300">
                    <h3 className="text-stone-700 font-semibold text-xs uppercase mb-1">
                      Deals done
                    </h3>
                    <p className="text-2xl font-medium text-stone-700">
                      {maturedDeals} <span className="text-black/30">of {totalDeals}</span>
                    </p>
                  </div>
                  <div className="p-4 md:p-6 border-r border-r-stone-300 max-xl:border-b max-xl:border-b-stone-300">
                    <h3 className="text-stone-700 font-semibold text-xs uppercase mb-1">Raised</h3>
                    <p className="text-2xl font-medium text-stone-700">
                      {totalRaisedByCompanyFormatted}
                    </p>
                  </div>
                  <div className="p-4 md:p-6 border-r border-r-stone-300">
                    <h3 className="text-stone-700 font-semibold text-xs uppercase mb-1">
                      Repayments
                    </h3>
                    <p className="text-2xl font-medium text-stone-700">
                      {repaymentsDone} of <span className="text-black/30">{totalRepayments}</span>
                    </p>
                  </div>
                  <div className="p-4 md:p-6">
                    <h3 className="text-stone-700 font-semibold text-xs uppercase mb-1">
                      On time payments
                    </h3>
                    <p className="text-2xl font-medium text-stone-700">
                      {onTimeRepaymentsPercentage} <span className="text-black/30">%</span>
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:col-span-1">
                  {clientImageUrl && (
                    <div className="p-4 md:p-6 border-r border-r-stone-300">
                      <h3 className="text-stone-700 font-semibold text-xs uppercase mb-4">
                        Clients
                      </h3>
                      <div className="h-16 mix-blend-darken max-w-[300px]">
                        {clientImageUrl && (
                          <Image
                            // @ts-ignore
                            src={clientImageUrl}
                            alt={dealDetails.name}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {backedByImageUrl && (
                    <div className="p-4 md:p-6">
                      <h3 className="text-stone-700 font-semibold text-xs uppercase mb-4">
                        Backed by
                      </h3>
                      <div className="h-16 mix-blend-darken max-w-[300px]">
                        {backedByImageUrl && (
                          <Image
                            // @ts-ignore
                            src={backedByImageUrl}
                            alt={dealDetails.name}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Pooling sections */}
          {isPooling && (
            <div className="mt-6">
              {/* How does this work */}
              <div className="border border-stone-200 flex overflow-hidden rounded-lg items-center">
                <div className="px-[24px]" id="deat-details-info-text">
                  <h3 className="text-stone-700 font-[600] text-[15px]">How does this work?</h3>
                  <p className="text-stone-500 mt-3 text-[15px] font-[400]">
                    A Multi-Deal bundles several Invoice Discounting deals that share the same
                    tenure. Your amount is distributed across these deals, and the overall XIRR is
                    the weighted average of their returns, giving you balanced, diversified exposure
                    in one investment.
                  </p>
                </div>
                <Image
                  // @ts-ignore
                  src={MultiCardAsset}
                  alt="How multi invoice works"
                  className="max-h-[150px] object-cover hidden md:block"
                />
              </div>

              {/* Companies */}
              <div className="mt-9">
                <h2 className="text-stone-800 text-lg font-[500]">Company wise Distribution</h2>
                <DealCompanies companies={constituentDTOList} />
              </div>
            </div>
          )}
          <div className="mt-6 md:mt-14">
            <h3 className="text-lg mb-3 text-slate-800 font-medium">Security Measures</h3>
            <div className="grid grid-cols-2 gap-7 mt-6">
              <SecurityMeasureCard
                className="security-measure-card"
                Icon={TimelineOutlinedIcon}
                tooltipTitle={'Risk Analysis'}
                tooltipContent={
                  'A Multi-Deal bundles several Invoice Discounting deals that share the same tenure. Your amount is distributed across these deals, and the overall XIRR is the weighted average of their returns, giving you balanced, diversified exposure in one investment.'
                }
              />
              <SecurityMeasureCard
                className="security-measure-card"
                Icon={ScreenRotationAltOutlinedIcon}
                tooltipTitle={'On Demand eNACH'}
                tooltipContent={
                  'On-demand eNACH is enabled for the entire exposure amount of the companies before they can raise capital through Tap Invest. An on-demand eNACH ensures that the payment can be triggered whenever needed, ensuring quick and efficient collections.'
                }
              />
              <SecurityMeasureCard
                className="security-measure-card"
                Icon={ArticleOutlinedIcon}
                tooltipTitle={'Undated Cheques'}
                tooltipContent={
                  'Undated cheques are collected from the companies for the entire exposure amount before they can raise capital through Tap Invest. These cheques help faciliate seamless and on-time collections for an invoice discounting deal, and account for penalties in rare instances of delay.'
                }
              />
            </div>
          </div>

          {hasDocuments && !isPooling && (
            <div className="mt-6 md:mt-14">
              <h3 className="text-lg mb-3 text-slate-800 font-medium" data-html2canvas-ignore>
                Read more about this company
              </h3>
              <p className="text-stone-500 mb-3" data-html2canvas-ignore>
                Looking for key metrics like <b>funding, traction, financials and more</b>?
              </p>
              <p className="text-stone-500" data-html2canvas-ignore>
                We do a complete deep dive of the companies listed on our platform as a part of our
                diligence process. Download the documents below.
              </p>

              {/* Downloadable files */}
              <div className="mt-4 md:mt-9 flex flex-wrap gap-6" id="deal-detail-documents">
                {documents &&
                  documents.map((document: DealDocumentType) => (
                    <a href={document.publicLink} target="_blank" rel="noreferrer" download>
                      <div
                        key={document.name}
                        className="border border-stone-200 md:w-96 rounded-lg py-5 px-4 flex justify-between items-center"
                      >
                        <div>
                          <h4 className="text-stone-700 font-medium text-sm">{document.name}</h4>
                          <p className="text-stone-500 text-sm">
                            {document.type}, {document.size}
                          </p>
                        </div>
                        <DownloadForOfflineOutlinedIcon className="text-stone-500" />
                      </div>
                    </a>
                  ))}
              </div>
            </div>
          )}

          {/* FAQs */}
          <div className="mt-9" data-html2canvas-ignore>
            <h2 className="text-stone-800 text-lg font-medium">Frequently Asked Questions</h2>
            {isPooling ? <MultiFaqs /> : <IDFaqs />}
          </div>
        </div>

        {/* Statistics / Create New Order */}
        {/* If we want to enable reinvestment again just replace the line with this
        <form
          className="lg:mt-12 max-lg:col-span-2"
          onSubmit={reinvest ? handleReinvestModal : handleSubmit}
        >
 */}
        <form className="lg:mt-12 max-lg:col-span-2" onSubmit={handleSubmit}>
          <div
            className="flex flex-col bg-black/[.02] rounded-md border border-stone-300"
            id="deal-detail-form"
          >
            {flatPercentageDealPartner.some((item) => item == dealId) && (
              <div className="bg-green-700 text-white p-4 md:p-2 text-sm font-semibold rounded-tl-md rounded-tr-md">
                Exclusive deal with 2% to 2.5% commission.
                <br />
                <span className="text-xs">*Terms and conditions apply.</span>
              </div>
            )}
            <div>
              {/* Order Investment Input */}
              <div
                ref={amountSectionRef}
                className="bg-white border-b rounded-t-lg relative text-slate-600 overflow-hidden h-40"
                data-html2canvas-ignore
              >
                <label className="absolute z-10 top-6 left-3 tracking-wider uppercase text-xs font-semibold text-black/40">
                  Enter investment amount
                </label>
                <div className="mt-10 pt-2">
                  <Input
                    ref={amountInputRef}
                    value={investmentAmount || ''}
                    prefixIcon={<span className="text-black/40 font-medium text-xl">₹</span>}
                    type="text"
                    placeholder={minInvestment.amount || ''}
                    className="font-semibold text-2xl placeholder:text-stone-400 border-none rounded-none"
                    containerClassName="border-none rounded-none !shadow-none"
                    onChange={handleInvestmentAmount}
                    onFocus={() => {
                      track({ eventName: DEAL_DETAIL_EVENTS.Deal_Detail_Investment_Amount });
                    }}
                  />
                </div>
                <div className="absolute bottom-6 left-4">
                  <button
                    type="button"
                    className="text-[12px] text-[#15803D]  rounded-full px-[14px] py-[4px] bg-green-700/10 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    onClick={handleUseWalletBalance}
                    disabled={!client}
                  >
                    Use Wallet Balance
                  </button>
                </div>

                {!!amountError && (
                  <span className="text-red-500 text-xs absolute bottom-[5px] left-4">
                    {amountError}
                  </span>
                )}
              </div>
              <div
                className="p-4 md:p-5 border-b border-b-stone-300 flex items-center gap-4 text-slate-600"
                data-html2canvas-ignore
              >
                <h3 className="text-sm min-w-36">Total Returns</h3>
                <span className="text-lg text-green-700 font-[600] line-clamp-1">
                  {totalReturns
                    ? getRupeeWithDecimal.format(totalReturns)
                    : getRupeeWithDecimal.format(minInvestment.returnAmount)}
                </span>
              </div>

              <div className="p-4 md:p-5 border-b border-b-stone-300 flex items-center gap-4 text-slate-600">
                <h3 className="text-sm min-w-36">Minimum Investment</h3>
                <span className="text-lg font-medium">
                  {minInvestment.amount ? getRupee.format(minInvestment.amount) : '-'}
                </span>
              </div>
              <div className="p-4 md:p-5 border-b border-b-stone-300 flex items-center gap-4 text-slate-600">
                <div className="flex items-center gap-1">
                  <h3 className="text-sm min-w-36">
                    Yield{' '}
                    <CustomTooltip
                      title={
                        <YieldTooltipContent
                          xirr={twoDecimalNumber(xirr)}
                          perAnnum={twoDecimalNumber(perAnnumReturns)}
                          roi={twoDecimalNumber(roi)}
                        />
                      }
                      placement="left"
                      arrow
                      data-html2canvas-ignore
                    >
                      <InfoIcon sx={{ height: '16px', width: '16px' }} className="text-stone-500" />
                    </CustomTooltip>
                  </h3>
                </div>

                <span className="text-lg font-medium">
                  {orderType === 'DOWN_SELL' && targetYield
                    ? `${targetYield}%`
                    : minInvestment.netIRR
                      ? `${twoDecimalNumber(minInvestment.netIRR)}%`
                      : '-'}
                </span>
              </div>
              <div className="p-4 md:p-5 flex items-center gap-4 text-slate-600">
                <h3 className="text-sm min-w-36">Payout on</h3>
                <span className="text-lg font-medium">
                  {maturityDate ? formatDate(maturityDate) : '-'}
                </span>
                {hasEarningsSection && <div className="ml-auto">{commissionToggle}</div>}
              </div>
              {hasEarningsSection && (
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                    isCommissionVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                  aria-hidden={!isCommissionVisible}
                  data-html2canvas-ignore
                >
                  <div className="overflow-hidden">
                    <div
                      className={`transition-all duration-300 ease-in-out ${
                        isCommissionVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
                      }`}
                    >
                      {showCommissionRow && (
                        <div
                          className="p-4 md:p-5 flex items-center gap-4 text-slate-600 border-t border-t-stone-300"
                        >
                          <h3 className="text-sm min-w-36 font-[400] text-green-700">Your Commission</h3>
                          <span className="text-lg font-medium text-stone-600 line-clamp-1">
                            {commission
                              ? getRupeeWithDecimal.format(commission)
                              : getRupeeWithDecimal.format(initialCommission)}{' '}
                            <span className="font-medium text-[12px] text-stone-500">
                              ({commissionRate ? commissionRate : commissionPercentage}%)
                            </span>
                          </span>
                        </div>
                      )}
                      {!isPooling && (
                        <div className="p-4 md:p-5 border-t border-t-stone-300 text-slate-600" data-html2canvas-ignore>
                          <h3 className="text-sm font-medium mb-3">How do you want to earn?</h3>
                          <div className="flex gap-2">
                            {!downSellEnabled && (
                              <button
                                type="button"
                                onClick={() => {
                                  setOrderType('COMMISSION');
                                  clearDownSellBreakdown();
                                }}
                                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold ${
                                  orderType === 'COMMISSION'
                                    ? 'border-green-700 bg-green-700/10 text-green-800'
                                    : 'border-stone-300 bg-white'
                                }`}
                              >
                                Commission
                              </button>
                            )}
                            {downSellEnabled && (
                              <button
                                type="button"
                                className="flex-1 rounded-lg border px-3 py-2 text-sm font-semibold border-green-700 bg-green-700/10 text-green-800"
                              >
                                Down-sell
                              </button>
                            )}
                          </div>
                          {orderType === 'DOWN_SELL' && minInvestment?.netIRR != null && (
                            <DownSellYieldScrubber
                              dealYield={Number(minInvestment.netIRR)}
                              value={targetYield}
                              onChange={setTargetYield}
                              onDragStateChange={setIsDownSellDragging}
                              presets={savedDownSells}
                              profit={downSellProfit}
                              grossProfit={grossDownSellProfit}
                              sharePct={blendedDownSellSharePct}
                              retained={platformRetainedDownSellProfit}
                              isUpdating={isDownSellTermsUpdating || isDownSellDragging}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
            )}
            </div>
          </div>
          {dealDetails?.company?.reinvestment && (
            <div className="mt-6">
              <div className="flex justify-between items-center rounded-md border border-stone-300 bg-black/[.02] p-5">
                <div className="flex gap-1 items-center">
                  <Tooltip
                    title="Automatically reinvest your payout in the same company. Your payout will be reinvested only if the XIRR is equal to or greater than the current investment and the tenure remains the same."
                    placement="top"
                    arrow
                  >
                    <span className="underline text-slate-600 text-sm">Re-invest</span>
                  </Tooltip>
                  {/* <span className="text-[8px] text-green-700 uppercase font-semibold py-0.5 px-1 bg-green-700/10">
                    New
                  </span> */}
                </div>
                <div>
                  <Switch
                    checked={reinvest}
                    onChange={handleReinvestChange}
                    classes={{
                      track: reinvest && '!bg-green-600 !opacity-100',
                      switchBase: '!text-white',
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>
            </div>
          )}
          <div className={`mt-6 `} data-html2canvas-ignore>
            <SelectClient {...selectClientProps} />
          </div>
          {!!client && client?.balance < parseFloat(investmentAmount) && (
            <p className="text-amber-700 mt-1.5 mb-2 text-sm font-semibold flex items-center gap-1">
              <InfoIcon fontSize="small" /> There's insufficient money in the client's wallet
            </p>
          )}
          <div className="mt-4" data-html2canvas-ignore>
            <Checkbox
              id="acceptTerms"
              className="accent-green-600"
              label={
                <span>
                  By proceeding you agree to our{' '}
                  <a href="/terms" target="_blank" className="underline">
                    Terms & Conditions
                  </a>{' '}
                  {!isPooling && (
                    <span>
                      and{' '}
                      <button
                        type="button"
                        onClick={(e) => {
                          openServiceAgreement();
                        }}
                        className="underline"
                      ></button>
                      Service Agreement
                    </span>
                  )}
                </span>
              }
              labelClassName="text-stone-500"
              defaultChecked
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
          </div>
          <div
            className="mt-4 flex flex-col justify-between items-center gap-2.5"
            data-html2canvas-ignore
          >
            <button
                onClick={() => {
                  const amountValue = sanitizeAmount(investmentAmount);
                  if (!investmentAmount || amountValue <= 0) {
                    focusInvestmentAmount('Please enter investment amount');
                    return;
                  }
                  if (minInvestment?.amount != null && amountValue < minInvestment.amount) {
                    focusInvestmentAmount('Less than minimum investment amount');
                    return;
                  }
                  if (!!client && !isClientWalletEnough) {
                    toast.error("There's insufficient money in the client's wallet", {
                      duration: 3000,
                    });
                    return;
                  }
                  handleInvest();
                }}
                type="button"
                className={`bg-white shadow flex-shrink-0 flex items-center justify-center gap-2 text-sm text-gray-700 border border-gray-300 rounded-xl font-semibold h-10 w-full px-4 ${
                  (!investmentAmount ||
                    sanitizeAmount(investmentAmount) < minInvestment.amount ||
                    !clientId ||
                    !partnerId ||
                    !!amountError ||
                    !acceptedTerms ||
                    buttonLoading ||
                    (!!client && !isClientWalletEnough) ||
                    (orderType === 'DOWN_SELL' &&
                      (!targetYield || Number(targetYield) <= 0 ||
                        (minInvestment?.netIRR != null &&
                          Number(targetYield) >= Number(minInvestment.netIRR))))
                  ) &&
                  'opacity-70 cursor-not-allowed'
                }`}
                disabled={
                  // Amount issues stay clickable so we can scroll + show the field error
                  !clientId ||
                  !partnerId ||
                  !acceptedTerms ||
                  buttonLoading ||
                  (!!client && !isClientWalletEnough) ||
                  (orderType === 'DOWN_SELL' &&
                    (!targetYield ||
                      Number(targetYield) <= 0 ||
                      (minInvestment?.netIRR != null &&
                        Number(targetYield) >= Number(minInvestment.netIRR))))
                }
              >
                Create New Order
              </button>
          </div>
        </form>

        {/* Client Wallet Details */}
        <Dialog open={walletOpen} maxWidth="sm" fullWidth onClose={handleWalletClose}>
          <div className="py-5 border bg-white border-zinc-200 rounded-lg">
            <div className="px-5 border-b border-b-black/10 pb-6">
              {virtualAccountLoading ? (
                <Skeleton width={80} height={40} />
              ) : (
                <h3 className="text-lg font-semibold">Wallet Details</h3>
              )}
              <p className="text-slate-600 text-sm mt-1.5">
                Your client needs to add money to the following beneficiary, to make investments.
              </p>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-center">
                <h4 className="text-sm text-gray-600 w-36">Name:</h4>
                <h3 className="text-sm font-medium">{client?.name}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-sm text-gray-600 w-36">Bank Name:</h4>
                <h3 className="text-sm font-medium">{walletBankName}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-sm text-gray-600 w-36">Branch Name:</h4>
                <h3 className="text-sm font-medium">{walletBranchDetails}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-sm text-gray-600 w-36">Account Number:</h4>
                <h3 className="text-sm font-medium">{walletAccountNumber}</h3>
              </div>
              <div className="flex items-center">
                <h4 className="text-sm text-gray-600 w-36">IFSC Code:</h4>
                <h3 className="text-sm font-medium">{walletIfscCode}</h3>
              </div>
            </div>
            <div className="px-5 pt-6 border-t border-black/10">
              <div className="flex items-center gap-3 justify-end">
                <a
                  href={`whatsapp://send?text=${waText}`}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-white text-slate-600 border border-black/10 shadow flex items-center gap-2 text-sm rounded-xl font-semibold px-4 h-10"
                >
                  Share via WhatsApp
                </a>
                <button
                  onClick={copyToClipboard}
                  className="bg-green-700 text-white text-sm font-semibold shadow h-10 px-4 rounded-xl"
                >
                  Copy All
                </button>
              </div>
            </div>
          </div>
        </Dialog>

        {/* Investment Status */}
        <Dialog open={!!investmentStatus} maxWidth="sm" fullWidth onClose={handleClose}>
          <div className="p-4 md:p-6 border bg-white border-zinc-200 rounded-lg">
            <div className="mb-4">
              {investmentStatus === 'success' && <VerifiedIcon className="text-green-700" />}
              {investmentStatus === 'error' && <CreditCardOffIcon className="text-red-700" />}
            </div>
            <div>
              <h3 className="font-semibold text-xl">
                {investmentStatus === 'success'
                  ? 'Thank you for investing with Tap Invest!'
                  : 'There was an error processing the payment.'}
              </h3>
              <p className="text-slate-600 text-sm max-w-md mt-4">
                {investmentStatus === 'success' ? (
                  <span>
                    {' '}
                    Your investment is being deployed as we speak. You can view your earning and
                    investment status in{' '}
                    <Link
                      to={`/clients/${client?.clientPublicId}`}
                      onClick={handleClose}
                      className="underline"
                    >
                      Dashboard
                    </Link>
                  </span>
                ) : (
                  'We apologise for the inconvenience caused, as there seems to be an issue on our end. Any money debited will be refunded.'
                )}
              </p>
              {investmentStatus === 'success' && (
                <p className="text-slate-600 font-semibold mt-4">
                  Your Investment ID: {investmentId}
                </p>
              )}

              {investmentStatus === 'success' ? (
                partner?.data?.partnerAggregate?.arrears > 0 && showPartnerArrearsCard ? (
                  <div className="flex justify-between items-center">
                    <p
                      className="text-[#D97706] py-2 px-2 bg-[#D977061F] text-[12px] flex gap-2 items-center mt-6"
                      style={{
                        border: '.5px solid #D97706',
                        borderRadius: '6px',
                      }}
                    >
                      <OfflineBoltRoundedIcon fontSize="small" className="text-[#D97706]" />
                      You are all set for Next Day Commission
                    </p>

                    <div className="flex gap-2">
                      <button
                        className="text-stone-600 bg-white mt-6 ml-auto flex items-center border border-black/10 h-10 text-sm font-semibold px-3 w-fit rounded-xl shadow-sm"
                        onClick={handleClose}
                      >
                        Invest More
                      </button>
                      <Link to={`/clients/${client?.clientPublicId}`} onClick={handleClose}>
                        <button className="bg-green-700 mt-6 ml-auto flex items-center h-10 text-white text-sm font-semibold px-3 w-fit rounded-xl shadow">
                          Go to Dashboard
                        </button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    <button
                      className="text-stone-600 bg-white mt-6 ml-auto flex items-center border border-black/10 h-10 text-sm font-semibold px-7 w-fit rounded-xl shadow-sm"
                      onClick={handleClose}
                    >
                      Invest More
                    </button>
                    <Link to={`/clients/${client?.clientPublicId}`} onClick={handleClose}>
                      <button className="bg-green-700 mt-6 ml-auto flex items-center h-10 text-white text-sm font-semibold px-7 w-fit rounded-xl shadow">
                        Go to Dashboard
                      </button>
                    </Link>
                  </div>
                )
              ) : (
                <Link to={'/investments'} onClick={handleClose}>
                  <button className="bg-green-700 mt-6 ml-auto flex items-center h-10 text-white text-sm font-semibold px-7 w-fit rounded-xl shadow">
                    Understood
                  </button>
                </Link>
              )}
            </div>
          </div>
        </Dialog>

        {/* Reinvest Modal */}
        <Dialog
          maxWidth="md"
          fullWidth
          open={reinvestModal}
          onClose={() => setReinvestModal(false)}
        >
          <div className="grid lg:grid-cols-4">
            <div className="hidden lg:block col-span-1">
              <img src={ReinvestBanner} alt="Re-invest" className="h-full" />
            </div>
            <div className="lg:col-span-3">
              <div className="p-6 border-b border-b-stone-200">
                {/* <h4 className="text-green-700 uppercase font-semibold text-xs">New feature</h4> */}
                <h2 className="text-xl font-semibold my-1.5">
                  Reinvest for your client ' {client?.name} '
                </h2>
                <div className="flex items-center gap-8">
                  <p className="text-stone-800 text-xs">
                    <span className="text-gray-700 font-medium opacity-40">Pan Number</span>{' '}
                    {client?.pan}
                  </p>
                  <p className="text-stone-800 text-xs">
                    <span className="text-gray-700 font-medium opacity-40">Phone Number</span>{' '}
                    {client?.phone}
                  </p>
                </div>
              </div>
              <div className="p-6">
                <p className="text-stone-500">
                  By enabling reinvest, your client's payout will automatically be invested again in
                  the same company on better or same terms.
                </p>

                <div className="flex justify-between items-center gap-10 mt-5">
                  <p className="text-stone-700 font-medium flex flex-col gap-1">
                    <span className="uppercase text-stone-400 font-semibold text-xs">Company</span>{' '}
                    {dealDetails?.company?.brandName}
                  </p>
                  <p className="text-stone-700 font-medium flex flex-col gap-1">
                    <span className="uppercase text-stone-400 font-semibold text-xs">IRR</span>{' '}
                    {minInvestment.netIRR}
                  </p>
                  <p className="text-stone-700 font-medium flex flex-col gap-1">
                    <span className="uppercase text-stone-400 font-semibold text-xs">Tenure</span>{' '}
                    {dealDetails?.tenure}
                  </p>
                </div>

                <div className="flex flex-col gap-2 mt-14">
                  <div className="px-2 text-stone-500 bg-stone-100 py-3 rounded-md font-medium text-xs flex items-center gap-2">
                    <InfoOutlinedIcon fontSize="small" />
                    <span>
                      By clicking "Proceed" you agree to the{' '}
                      <a href="/terms" target="_blank" className="underline">
                        Terms & Conditions
                      </a>
                      .
                    </span>
                  </div>
                  <div className="px-2 py-3 text-green-700 bg-green-600/[0.08] rounded-md font-medium text-xs flex items-center gap-2">
                    <InfoOutlinedIcon fontSize="small" />
                    <span>You can always opt out later, upto 2 days before payouts.</span>
                  </div>
                </div>
              </div>
              <div className="flex w-full p-6 border-t border-t-stone-200 justify-end gap-2">
                <button
                  className="bg-white shadow-sm border border-slate-200 rounded-xl h-10 px-7 text-sm text-slate-600 font-semibold"
                  onClick={handleOptOut}
                  type="button"
                >
                  Opt out
                </button>
                <button
                  className="bg-green-600 rounded-xl h-10 px-7 text-sm text-white font-semibold"
                  type="button"
                  onClick={handleOptIn}
                >
                  Opt In & Proceed
                </button>
              </div>
            </div>
          </div>
        </Dialog>
      </div>

      <ClientDrawer />

      {/* wallet recharge modal */}
      <Dialog open={isRechargeModalOpen} maxWidth="sm" fullWidth>
        <div className="flex bg-amber-100 p-2.5 items-center gap-2 ">
          <InfoIcon
            className="text-amber-700"
            sx={{
              height: '16px',
              width: '16px',
            }}
          />
          <h4 className="text-sm text-amber-700 font-medium">
            This link is valid only for 6 hours
          </h4>
        </div>
        <DialogTitle>
          <div className="px-5 flex justify-between">
            <div>
              <h3 className="text-lg font-semibold">Link Generated</h3>
              <p className="text-slate-600 text-sm mt-1.5">
                {isClientWalletEnough
                  ? 'Copy link to let your client make an investment on the deal.'
                  : "Copy link to receive money in the client's wallet."}
              </p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={() => handleWalletRechargeClose()}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="py-5bg-white rounded-lg">
            <div className="p-5 pb-3 flex gap-3">
              <div
                className="flex flex-col justify-between gap-[6px]  flex-1"
                style={{
                  padding: '14px 16px 16px 0px',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                <h4 className="text-sm text-stone-400 ">Wallet top-up Amount </h4>
                <h3 className="text-base font-medium text-green-700 tracking-wider">
                  {getRupeeWithDecimal.format(parseFloat(investmentAmount))}
                </h3>
              </div>
              <div
                className="flex flex-col justify-between gap-[6px] flex-1"
                style={{ padding: '14px 16px 16px 24px' }}
              >
                <h4 className="text-sm text-stone-400">Current Balance</h4>
                <h3 className="text-base font-medium tracking-wider">
                  {getRupeeWithDecimal.format(client?.balance || 0)}
                </h3>
              </div>
            </div>
            <div className="px-5 pt-6 pb-3">
              <div className="flex flex-col gap-[6px]">
                <h4 className="text-sm text-gray-600">Share link with your client</h4>
                <div className="flex items-center w-full">
                  <input
                    type="text"
                    value={`${userPaymentLink || 'loading link...'}`}
                    readOnly
                    className="flex-grow border border-gray-300 rounded-l-xl p-2"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(userPaymentLink);
                      track({ eventName: CLIENT_DETAIL_EVENTS.Client_Copy_url });
                      toast.success('Link copied to clipboard', {
                        duration: 2000,
                        position: 'top-center',
                      });
                    }}
                    className="bg-green-700 text-white text-sm font-semibold h-[2.65rem] px-4 rounded-r-xl "
                  >
                    Copy Url
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* MIF payment link modal */}
      <Dialog open={isPaymentOpen} maxWidth="sm" fullWidth>
        <div className="flex bg-amber-100 p-2.5 items-center gap-2 ">
          <InfoIcon
            className="text-amber-700"
            sx={{
              height: '16px',
              width: '16px',
            }}
          />
          <h4 className="text-sm text-amber-700 font-medium">
            This link is valid only for 6 hours
          </h4>
        </div>
        <DialogTitle>
          <div className="px-5 flex justify-between">
            <div>
              <h3 className="text-lg font-semibold">Link Generated</h3>
              <p className="text-slate-600 text-sm mt-1.5">
                Copy link to let your client make an investment on the deal.
              </p>
            </div>
            <CloseOutlinedIcon
              className="cursor-pointer bg-slate-200 rounded-full p-1"
              onClick={() => handlePaymentClose()}
              style={{
                width: '28px',
                height: '28px',
              }}
            />
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="py-5bg-white rounded-lg">
            <div className="p-5 pb-3 flex gap-3">
              <div
                className="flex flex-col justify-between gap-[6px]  flex-1"
                style={{
                  padding: '14px 16px 16px 0px',
                  borderRight: '1px solid #f0f0f0',
                }}
              >
                <h4 className="text-sm text-stone-400 ">Deal Name</h4>
                <h3 className="text-base font-medium text-green-700 tracking-wider">
                  {dealDetails?.company?.brandName}
                </h3>
              </div>
              <div
                className="flex flex-col justify-between gap-[6px] flex-1"
                style={{ padding: '14px 16px 16px 24px' }}
              >
                <h4 className="text-sm text-stone-400">Investment Amount</h4>
                <h3 className="text-base font-medium tracking-wider">
                  {getRupeeWithDecimal.format(parseFloat(investmentAmount))}
                </h3>
              </div>
            </div>
            <div className="px-5 pt-6 pb-3">
              <div className="flex flex-col gap-[6px]">
                <h4 className="text-sm text-gray-600">Share link with your client</h4>
                <div className="flex items-center w-full">
                  <input
                    type="text"
                    value={`${userPaymentLink || 'loading link...'}`}
                    readOnly
                    className="flex-grow border border-gray-300 rounded-l-xl p-2"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(userPaymentLink);
                      track({ eventName: CLIENT_DETAIL_EVENTS.Client_Copy_url });
                      toast.success('Link copied to clipboard', {
                        duration: 2000,
                        position: 'top-center',
                      });
                    }}
                    className="bg-green-700 text-white text-sm font-semibold h-[2.65rem] px-4 rounded-r-xl "
                  >
                    Copy Url
                  </button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

DealDetail.displayName = 'DealDetail';

export default React.memo(DealDetail);
